<template>
  <div class="statusCard shadowed">
    <div class="headerRow">
      <!-- Name / Date -->
      <div class="nameAndCreated">
        <div class="name">{{ application.name }}</div>
        <div class="created pt-1">
          <span class="createdLabel">Started: </span>
          <span class="createdDate">{{ formatDateAs(application.submittedAt, "MM/DD/YYYY") }}</span>
        </div>

        <div v-if="application.statusMessage" class="statusMessage mt-2 is-flex is-flex-direction-row">
          <i class="fas fa-exclamation-circle mt-1 mr-2"></i>
          <div v-html="new MarkdownIt().render(application.statusMessage)"></div>
        </div>
      </div>

      <!-- Status -->
      <div class="status">
        <div class="statusLabel">Current Status:</div>
        <div style="width: fit-content" class="statusName my-1" :class="currentStatus!.type">
          {{ currentStatus!.label }}
        </div>
      </div>
    </div>

    <div v-if="keyDocument?.attachments.length" class="documentSection">
      <div class="statusLabel pb-2">Your documents are available to download:</div>
      <DocumentUploadWidget :documents="keyDocument?.attachments" @view="downloadDocument($event)" readonly />
    </div>

    <OnlineSocialSecurityApplicationStatus v-if="application.applicationType == Core.EntityType['Online Social Security']" :application="application" @changed="emit('changed')"/>

    <div v-if="opened" class="statusHistory">
      <table style="text-align: left;">
        <template v-for="(item,idx) in application.history" :key="idx">
          <tr :class="item.type">
            <td v-if="idx == 0" class="historyPipe historyPipeDown"><div class="dot"></div></td>
            <td v-else-if="idx < application.history.length-1" class="historyPipe historyPipeBoth"><div class="dot"></div></td>
            <td v-else class="historyPipe historyPipeUp"><div class="dot"></div></td>
            <td class="historyLabel">{{ item.label }}</td>
          </tr>
          <tr>
            <td v-if="idx != application.history.length-1" class="historyPipe historyPipeBoth"></td>
            <td v-else></td>
            <td class="historyDescription">{{ item.date ? `${formatDateAs(item.date, 'MM/DD/YYYY')}: ` : ''}}{{ item.description }}</td>
          </tr>
          <tr v-if="idx != application.history.length-1">
            <td class="historyPipe historyPipeBoth"></td>
            <td></td>
          </tr>
        </template>
      </table>
    </div>

    <i v-if="opened" @click="opened=false" class="toggleButton fas fa-chevron-up"></i>
    <i v-else @click="opened=true" class="toggleButton fas fa-chevron-down"></i>
  </div>
</template>

<script lang="ts" setup>
import { formatDateAs, notifyNetworkError } from '@/core/utils';
import { ApplicationStatus, Core } from "@minicity/core";
import { computed, ref } from 'vue';
import OnlineSocialSecurityApplicationStatus from '@/features/applications/onlineSocialSecurity/OnlineSocialSecurityApplicationStatus.vue';
import MarkdownIt from "markdown-it";
import { sessionState } from '@/core/session';
import DocumentUploadWidget from '@/components/DocumentUploadWidget.vue';
import { getAttachmentViewLink } from './api';

const props = defineProps<{
  application: ApplicationStatus.Report,
}>();

const emit = defineEmits(['changed']);

const currentStatus = computed(() => {
  let status = null;
  for (let entry of props.application.history) {
    if (entry.type != "FUTURE") { status = entry; }
  }
  return status;
});

const opened = ref<boolean>(false);

const keyDocument = computed(() => {
  switch (props.application.applicationType) {
    case Core.EntityType['Birth Record']:
      return sessionState.session?.user.keyDocuments["BIRTH_RECORD"];
    case Core.EntityType['Georgia DFCS Benefits']:
      return sessionState.session?.user.keyDocuments["GEORGIA_DFCS_BENEFITS"];
    case Core.EntityType['Social Security']:
    case Core.EntityType['Online Social Security']:
      return sessionState.session?.user.keyDocuments["SOCIAL_SECURITY"];
    default:
      return undefined;
  }
});

const downloadDocument = (doc: Core.Document) => {
  getAttachmentViewLink(doc)
    .then((attachmentViewLink: string) => window.open(attachmentViewLink))
    .catch(err => notifyNetworkError("Failed to download attachment", err));
};

</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.statusCard {
  border: 1px solid rgb(180,210,180);
  border-radius: 1rem;
  width: min(100%, 800px);
  padding: 0rem;
}

.headerRow {
  display: flex;
  flex-direction: row;
  padding: 2.5rem 2.5rem 1.5rem 2.5rem;
  text-align: left;
}

.nameAndCreated {
  width: 65%;
}

.status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  
  width: 35%;
}

@media screen and (max-width: 800px) {
  .headerRow {
    flex-direction: column;
    text-align: center;
    padding: 1rem;
  }

  .nameAndCreated {
    width: 100%;
    margin-bottom: 1rem;
  }

  .status {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: unset;
  }

  .statusName {
    align-self: center;
  }
}

.documentSection {
  padding: 1rem 2.5rem 1.5rem 2.5rem;
  text-align: left;
}

.name {
  font-size: 125%;
  font-weight: bold;
}

.created {
  font-weight: bold;
}
.statusLabel {
  font-weight: bold;
  font-size: 1.10rem;
}

.statusMessage {
  padding: 1rem;
  background-color: #ffe2f4;
  border-radius: 1rem;
}

.statusHistory {
  padding: 0.5rem 2.5rem 0rem 2.5rem;
}

.historyLabel {
  padding-left: 0.75rem;
  font-weight: bold;
}

.historyDescription {
  padding-left: 0.75rem;
  font-size: 80%;
  padding-bottom: 1rem;
}

.historyPipeBoth {
  background: linear-gradient(#000, #000) no-repeat center/2px 100%;
}

.historyPipeDown {
  background: linear-gradient(#000, #000) no-repeat center 125%/2px 50%;
}

.historyPipeUp {
  background: linear-gradient(#000, #000) no-repeat center 0%/2px 50%;
}

.dot {
  height: 10px;
  width: 10px;
  background-color: #000;
  border-radius: 50%;
  display: inline-block;
}

td {
  text-align: left;
  padding: 0px;
}

.ACTION_REQUIRED > td > .dot {
  background-color: #b98d12;
}

.FUTURE > td > .dot {
  background-color: white;
  border: 2px solid black;
}

.FUTURE {
  color: #bbb
}

.statusName {
  padding: 0.25rem 1.25rem 0.25rem 1.25rem;
  background-color: #006600;
  border-radius: 1rem;

  min-width: 8rem;
  text-align: center;

  font-weight: bold;
  font-size: 0.9rem;
  color: white;
}

.statusName.ACTION_REQUIRED {
  background-color: #b98d12;
}

.toggleButton {
  cursor: pointer;
  padding: 1rem;
  width: 100%;
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;

  text-align: center;
}

.toggleButton:hover {
  background-color: rgba(0,0,0,20%);
}
</style>
